import { c as _c } from "react/compiler-runtime";
import { Badge } from "@osmix/ui/badge";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Input } from "@osmix/ui/input";
import { Label } from "@osmix/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Eye, EyeOff, Grid2x2, Image as ImageIcon, LayoutGrid, Lightbulb, type LucideIcon, Monitor, Pencil, Plus, ShoppingBag, Smartphone, Sparkles, Trash2, Upload, UserRound, X } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type CreateFeedBlockRequest, type FeedBlockAdmin, type FeedBlockKind, type FeedDepartment, type FeedPlacement, type FeedSegment, type FeedSlideWire, type FeedTileWire, type UpdateFeedBlockRequest } from "@/api";
import { feedKeys, useFeedBlocks } from "@/api/hooks/use-feed";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { PersonalFeedSettingsCard } from "@/components/personal-feed-settings";
import { Segmented } from "@/components/segmented";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
const PLACEMENT: FeedPlacement = "home";
const KINDS: FeedBlockKind[] = ["campaign", "ideaRail", "categoryRow", "collectionMosaic", "productRail", "photoBanner", "personalFeed"];
const SEGMENTS: FeedSegment[] = ["womens", "mens", "kids"];
const KIND_ICON: Record<FeedBlockKind, LucideIcon> = {
  campaign: Sparkles,
  ideaRail: Lightbulb,
  categoryRow: LayoutGrid,
  collectionMosaic: Grid2x2,
  productRail: ShoppingBag,
  photoBanner: ImageIcon,
  personalFeed: UserRound
};

// Цветовой код сегмента: общий — нейтральный, женское/мужское/детское — свои
// оттенки, чтобы блоки одного сегмента считывались взглядом в общем списке.
const SEGMENT_CHIP: Record<string, string> = {
  general: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  womens: "bg-pink-100 text-pink-700 dark:bg-pink-950 dark:text-pink-300",
  mens: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300",
  kids: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"
};
const SEGMENT_DOT: Record<string, string> = {
  general: "bg-slate-400",
  womens: "bg-pink-500",
  mens: "bg-sky-500",
  kids: "bg-amber-500"
};
type SegmentFilter = "all" | FeedSegment | "general";
type ContentMode = "slides" | "tiles" | "products" | "none";
const contentMode = (kind: FeedBlockKind): ContentMode => {
  if (kind === "campaign" || kind === "photoBanner") return "slides";
  if (kind === "ideaRail" || kind === "categoryRow" || kind === "collectionMosaic") return "tiles";
  if (kind === "productRail") return "products";
  return "none";
};
const rowId = (prefix: string) => `${prefix}_${crypto.randomUUID().slice(0, 8)}`;

// Переставить элемент массива на delta позиций, вернув новый массив.
const moveInArray = <T,>(list: T[], index: number, delta: number): T[] => {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(index, 1);
  if (item === undefined) return list;
  next.splice(target, 0, item);
  return next;
};

// Локальная редактируемая форма блока. Собирается из FeedBlockAdmin при
// открытии диалога и раскладывается обратно в Create/Update запрос при
// сохранении. Содержимое по типу блока разведено на три независимых списка —
// в запрос уходит только тот, что соответствует kind.
type Draft = {
  id: string | null;
  kind: FeedBlockKind;
  department: FeedDepartment;
  segment: FeedSegment | null;
  title: string;
  description: string;
  actionKind: "" | "link" | "chevron";
  actionLabel: string;
  published: boolean;
  slides: FeedSlideWire[];
  items: FeedTileWire[];
  // Товары держим как строки со стабильным id: список позиционный (add/remove
  // из середины), а сами значения могут повторяться или быть пустыми — индекс в
  // key дал бы сбой фокуса. В payload уходит только массив значений.
  productRows: {
    id: string;
    value: string;
  }[];
};
const draftFromBlock = (block: FeedBlockAdmin): Draft => ({
  id: block.id,
  kind: block.kind,
  department: block.department,
  segment: block.segment,
  title: block.title ?? "",
  description: block.description ?? "",
  actionKind: block.action?.kind ?? "",
  actionLabel: block.action?.label ?? "",
  published: block.published,
  slides: block.payload.slides ? block.payload.slides.map(s => ({
    ...s
  })) : [],
  items: block.payload.items ? block.payload.items.map(i => ({
    ...i
  })) : [],
  productRows: (block.payload.productIds ?? []).map(value => ({
    id: rowId("pid"),
    value
  }))
});
const emptyDraft = (department: FeedDepartment = "clothes"): Draft => ({
  id: null,
  kind: "campaign",
  department,
  segment: null,
  title: "",
  description: "",
  actionKind: "",
  actionLabel: "",
  published: true,
  slides: [],
  items: [],
  productRows: []
});
export function FeedPage() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const {
    data,
    error: loadError,
    isPending
  } = useFeedBlocks(PLACEMENT);
  const blocks = data ?? [];
  const [department, setDepartment] = useState<FeedDepartment>("clothes");
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>("all");
  const [draft, setDraft] = useState<Draft | null>(null);
  const [pendingRemove, setPendingRemove] = useState<FeedBlockAdmin | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const error = actionError ?? (loadError ? loadError.message : null);
  const invalidate = () => queryClient.invalidateQueries({
    queryKey: feedKeys.list(PLACEMENT)
  });

  // Блоки принадлежат департаменту («Одежда»/«Дом»); внутри — фильтр по сегменту.
  const inDepartment = blocks.filter(b => b.department === department);
  const matchesFilter = (block: FeedBlockAdmin) => {
    if (segmentFilter === "all") return true;
    if (segmentFilter === "general") return block.segment === null;
    return block.segment === segmentFilter;
  };
  const visible = inDepartment.filter(matchesFilter);
  const segmentCount = (filter: SegmentFilter) => filter === "all" ? inDepartment.length : inDepartment.filter(b_0 => matchesSegment(b_0, filter)).length;
  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= blocks.length) return;
    const next = moveInArray(blocks, index, delta);
    setBusy(true);
    setActionError(null);
    try {
      queryClient.setQueryData(feedKeys.list(PLACEMENT), await api.feed.reorder(PLACEMENT, next.map(block_0 => block_0.id)));
    } catch (e) {
      setActionError((e as Error).message);
      await invalidate();
    } finally {
      setBusy(false);
    }
  };
  const togglePublished = async (block_1: FeedBlockAdmin) => {
    setBusy(true);
    setActionError(null);
    try {
      await api.feed.update(block_1.id, {
        published: !block_1.published
      });
      await invalidate();
    } catch (e_0) {
      setActionError((e_0 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const removeBlock = async () => {
    if (!pendingRemove) return;
    setBusy(true);
    setActionError(null);
    try {
      await api.feed.remove(pendingRemove.id);
      setPendingRemove(null);
      await invalidate();
    } catch (e_1) {
      setActionError((e_1 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const saveDraft = async () => {
    if (!draft) return;
    setBusy(true);
    setActionError(null);
    const mode = contentMode(draft.kind);
    const payload: CreateFeedBlockRequest["payload"] = {};
    if (mode === "slides") {
      payload.slides = draft.slides.filter(s => s.imageUrl.trim());
    } else if (mode === "tiles") {
      payload.items = draft.items.filter(i => i.imageUrl.trim());
    } else if (mode === "products") {
      payload.productIds = draft.productRows.map(row => row.value.trim()).filter(Boolean);
    }
    const action = draft.actionKind === "" && !draft.actionLabel.trim() ? null : {
      kind: draft.actionKind === "" ? undefined : draft.actionKind,
      label: draft.actionLabel.trim() ? draft.actionLabel.trim() : undefined
    };
    try {
      if (draft.id === null) {
        const body: CreateFeedBlockRequest = {
          placement: PLACEMENT,
          department: draft.department,
          segment: draft.segment,
          kind: draft.kind,
          title: draft.title.trim() ? draft.title.trim() : null,
          description: draft.description.trim() ? draft.description.trim() : null,
          action,
          payload,
          published: draft.published
        };
        await api.feed.create(body);
      } else {
        const patch: UpdateFeedBlockRequest = {
          department: draft.department,
          segment: draft.segment,
          title: draft.title.trim() ? draft.title.trim() : null,
          description: draft.description.trim() ? draft.description.trim() : null,
          action,
          payload,
          published: draft.published
        };
        await api.feed.update(draft.id, patch);
      }
      setDraft(null);
      await invalidate();
    } catch (e_2) {
      setActionError((e_2 as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const segmentOptions: {
    value: SegmentFilter;
    label: string;
  }[] = [{
    value: "all",
    label: `${t("feed.segment.all")} · ${segmentCount("all")}`
  }, {
    value: "general",
    label: `${t("feed.segment.general")} · ${segmentCount("general")}`
  }, ...SEGMENTS.map(s_0 => ({
    value: s_0 as SegmentFilter,
    label: `${t(`feed.segment.${s_0}`)} · ${segmentCount(s_0)}`
  }))];
  return <div className="space-y-6">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1" data-tour="feed-intro">
          <h1 className="text-xl font-semibold">{t("feed.heading")}</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{t("feed.description")}</p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <Monitor className="size-3.5" /> {t("feed.channelWeb")}
            </span>
            <span className="inline-flex items-center gap-1">
              <Smartphone className="size-3.5" /> {t("feed.channelApp")}
            </span>
            <span>{t("feed.channelHint")}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{t("feed.blockCount", {
            count: blocks.length
          })}</Badge>
          <Button data-tour="feed-create" onClick={() => setDraft(emptyDraft(department))} disabled={busy}>
            <Plus className="size-4" /> {t("feed.create")}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("feed.placement")}</span>
          <Select value={PLACEMENT} disabled className="w-36">
            <option value="home">{t("feed.placementHome")}</option>
          </Select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground">{t("feed.department")}</span>
          <Segmented value={department} options={[{
          value: "clothes",
          label: t("feed.departmentClothes")
        }, {
          value: "home",
          label: t("feed.departmentHome")
        }, {
          value: "beauty",
          label: t("feed.departmentBeauty")
        }]} onChange={value => {
          setDepartment(value);
          setSegmentFilter("all");
        }} />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-tour="feed-segments">
          <span className="text-sm text-muted-foreground">{t("feed.segmentFilter")}</span>
          <Segmented value={segmentFilter} options={segmentOptions} onChange={value_0 => setSegmentFilter(value_0)} className="flex-wrap" />
        </div>
        {segmentFilter !== "all" && <span className="text-xs text-muted-foreground">{t("feed.reorderHint")}</span>}
      </div>

      <PersonalFeedSettingsCard />

      {error && <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>}

      {isPending ? <p className="text-sm text-muted-foreground">{t("feed.loading")}</p> : visible.length === 0 ? <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-muted">
            <LayoutGrid className="size-6 text-muted-foreground" />
          </div>
          <div className="space-y-1">
            <p className="font-medium">{t("feed.emptyTitle")}</p>
            <p className="mx-auto max-w-md text-sm text-muted-foreground">{t("feed.empty")}</p>
          </div>
          <Button onClick={() => setDraft(emptyDraft(department))} disabled={busy}>
            <Plus className="size-4" /> {t("feed.create")}
          </Button>
        </div> : <ol className="space-y-3" data-tour="feed-list">
          {visible.map(block_2 => {
        const fullIndex = blocks.findIndex(b_1 => b_1.id === block_2.id);
        return <FeedBlockCard key={block_2.id} block={block_2} index={fullIndex} total={blocks.length} busy={busy} canReorder={segmentFilter === "all"} onMoveUp={() => void move(fullIndex, -1)} onMoveDown={() => void move(fullIndex, 1)} onTogglePublished={() => void togglePublished(block_2)} onEdit={() => setDraft(draftFromBlock(block_2))} onRemove={() => setPendingRemove(block_2)} />;
      })}
        </ol>}

      {draft && <FeedBlockEditor draft={draft} busy={busy} onChange={setDraft} onClose={() => setDraft(null)} onSave={() => void saveDraft()} />}

      <ConfirmDialog open={!!pendingRemove} busy={busy} title={t("feed.confirmRemoveTitle")} description={t("feed.confirmRemoveBody")} confirmLabel={t("feed.remove")} confirmVariant="destructive" onConfirm={() => void removeBlock()} onClose={() => setPendingRemove(null)} />
    </div>;
}
const matchesSegment = (block: FeedBlockAdmin, filter: SegmentFilter) => {
  if (filter === "all") return true;
  if (filter === "general") return block.segment === null;
  return block.segment === filter;
};

// Значок сегмента с цветовой точкой — общий для карточки и предпросмотра.
function SegmentChip(t0) {
  const $ = _c(13);
  const {
    segment
  } = t0;
  const {
    t
  } = useTranslation();
  const key = segment ?? "general";
  const t1 = SEGMENT_CHIP[key];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = SEGMENT_DOT[key];
  let t4;
  if ($[2] !== t3) {
    t4 = cn("size-1.5 rounded-full", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4) {
    t5 = <span className={t4} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `feed.segment.${key}`;
  let t7;
  if ($[6] !== t || $[7] !== t6) {
    t7 = t(t6);
    $[6] = t;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t2 || $[10] !== t5 || $[11] !== t7) {
    t8 = <span className={t2}>{t5}{t7}</span>;
    $[9] = t2;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function FeedBlockCard(t0) {
  const $ = _c(119);
  const {
    block,
    index,
    total,
    busy,
    canReorder,
    onMoveUp,
    onMoveDown,
    onTogglePublished,
    onEdit,
    onRemove
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== block.kind) {
    t1 = contentMode(block.kind);
    $[0] = block.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mode = t1;
  const KindIcon = KIND_ICON[block.kind];
  let t2;
  if ($[2] !== block.payload.items?.length || $[3] !== block.payload.productIds?.length || $[4] !== block.payload.slides?.length || $[5] !== mode || $[6] !== t) {
    t2 = mode === "slides" ? t("feed.slidesCount", {
      count: block.payload.slides?.length ?? 0
    }) : mode === "tiles" ? t("feed.itemsCount", {
      count: block.payload.items?.length ?? 0
    }) : mode === "products" ? t("feed.productsCount", {
      count: block.payload.productIds?.length ?? 0
    }) : t("feed.noContent");
    $[2] = block.payload.items?.length;
    $[3] = block.payload.productIds?.length;
    $[4] = block.payload.slides?.length;
    $[5] = mode;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const contentSummary = t2;
  const t3 = !block.published && "border-dashed bg-muted/20";
  let t4;
  if ($[8] !== t3) {
    t4 = cn("flex flex-col gap-3 rounded-lg border p-4 transition-shadow hover:shadow-sm", t3);
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = index + 1;
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== KindIcon) {
    t7 = <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"><KindIcon className="size-4" /></span>;
    $[12] = KindIcon;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== block.kind || $[15] !== block.title || $[16] !== t) {
    t8 = block.title || t(`feed.kind.${block.kind}`);
    $[14] = block.kind;
    $[15] = block.title;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t8) {
    t9 = <span className="font-medium">{t8}</span>;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const t10 = `feed.kind.${block.kind}`;
  let t11;
  if ($[20] !== t || $[21] !== t10) {
    t11 = t(t10);
    $[20] = t;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11) {
    t12 = <Badge variant="outline">{t11}</Badge>;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== block.segment) {
    t13 = <SegmentChip segment={block.segment} />;
    $[25] = block.segment;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const t14 = block.published ? "default" : "outline";
  let t15;
  if ($[27] !== block.published) {
    t15 = block.published ? <Eye className="size-3" /> : <EyeOff className="size-3" />;
    $[27] = block.published;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== block.published || $[30] !== t) {
    t16 = block.published ? t("feed.published") : t("feed.unpublished");
    $[29] = block.published;
    $[30] = t;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t14 || $[33] !== t15 || $[34] !== t16) {
    t17 = <Badge variant={t14} className="gap-1">{t15}{t16}</Badge>;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t17 || $[39] !== t9) {
    t18 = <div className="flex flex-wrap items-center gap-2">{t9}{t12}{t13}{t17}</div>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t17;
    $[39] = t9;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== block.description) {
    t19 = block.description && <p className="line-clamp-2 text-sm text-muted-foreground">{block.description}</p>;
    $[41] = block.description;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== contentSummary) {
    t20 = <p className="text-xs text-muted-foreground">{contentSummary}</p>;
    $[43] = contentSummary;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t18 || $[46] !== t19 || $[47] !== t20) {
    t21 = <div className="min-w-0 flex-1 space-y-1.5">{t18}{t19}{t20}</div>;
    $[45] = t18;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  const t22 = busy || !canReorder || index === 0;
  let t23;
  if ($[49] !== t) {
    t23 = t("feed.moveUp");
    $[49] = t;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== canReorder || $[52] !== t) {
    t24 = canReorder ? t("feed.moveUp") : t("feed.reorderHint");
    $[51] = canReorder;
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <ArrowUp className="size-4" />;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] !== onMoveUp || $[56] !== t22 || $[57] !== t23 || $[58] !== t24) {
    t26 = <Button size="icon-sm" variant="ghost" onClick={onMoveUp} disabled={t22} aria-label={t23} title={t24}>{t25}</Button>;
    $[55] = onMoveUp;
    $[56] = t22;
    $[57] = t23;
    $[58] = t24;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  const t27 = busy || !canReorder || index === total - 1;
  let t28;
  if ($[60] !== t) {
    t28 = t("feed.moveDown");
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== canReorder || $[63] !== t) {
    t29 = canReorder ? t("feed.moveDown") : t("feed.reorderHint");
    $[62] = canReorder;
    $[63] = t;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ArrowDown className="size-4" />;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== onMoveDown || $[67] !== t27 || $[68] !== t28 || $[69] !== t29) {
    t31 = <Button size="icon-sm" variant="ghost" onClick={onMoveDown} disabled={t27} aria-label={t28} title={t29}>{t30}</Button>;
    $[66] = onMoveDown;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t26 || $[72] !== t31) {
    t32 = <div className="flex shrink-0 items-center gap-1">{t26}{t31}</div>;
    $[71] = t26;
    $[72] = t31;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[74] !== t21 || $[75] !== t32 || $[76] !== t6 || $[77] !== t7) {
    t33 = <div className="flex items-start gap-3">{t6}{t7}{t21}{t32}</div>;
    $[74] = t21;
    $[75] = t32;
    $[76] = t6;
    $[77] = t7;
    $[78] = t33;
  } else {
    t33 = $[78];
  }
  let t34;
  if ($[79] !== block.payload.slides) {
    t34 = block.payload.slides ?? [];
    $[79] = block.payload.slides;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== block.payload.items) {
    t35 = block.payload.items ?? [];
    $[81] = block.payload.items;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  const t36 = block.payload.productIds?.length ?? 0;
  let t37;
  if ($[83] !== block.kind || $[84] !== block.title || $[85] !== t34 || $[86] !== t35 || $[87] !== t36) {
    t37 = <div className="pl-9"><BlockPreview kind={block.kind} title={block.title} slides={t34} items={t35} productCount={t36} /></div>;
    $[83] = block.kind;
    $[84] = block.title;
    $[85] = t34;
    $[86] = t35;
    $[87] = t36;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== block.published || $[90] !== t) {
    t38 = block.published ? <><EyeOff className="size-4" /> {t("feed.unpublish")}</> : <><Eye className="size-4" /> {t("feed.publish")}</>;
    $[89] = block.published;
    $[90] = t;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  let t39;
  if ($[92] !== busy || $[93] !== onTogglePublished || $[94] !== t38) {
    t39 = <Button size="sm" variant="outline" onClick={onTogglePublished} disabled={busy}>{t38}</Button>;
    $[92] = busy;
    $[93] = onTogglePublished;
    $[94] = t38;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = <Pencil className="size-4" />;
    $[96] = t40;
  } else {
    t40 = $[96];
  }
  let t41;
  if ($[97] !== t) {
    t41 = t("feed.edit");
    $[97] = t;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== busy || $[100] !== onEdit || $[101] !== t41) {
    t42 = <Button size="sm" variant="outline" onClick={onEdit} disabled={busy}>{t40} {t41}</Button>;
    $[99] = busy;
    $[100] = onEdit;
    $[101] = t41;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <Trash2 className="size-4" />;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== t) {
    t44 = t("feed.remove");
    $[104] = t;
    $[105] = t44;
  } else {
    t44 = $[105];
  }
  let t45;
  if ($[106] !== busy || $[107] !== onRemove || $[108] !== t44) {
    t45 = <Button size="sm" variant="outline" onClick={onRemove} disabled={busy}>{t43} {t44}</Button>;
    $[106] = busy;
    $[107] = onRemove;
    $[108] = t44;
    $[109] = t45;
  } else {
    t45 = $[109];
  }
  let t46;
  if ($[110] !== t39 || $[111] !== t42 || $[112] !== t45) {
    t46 = <div className="flex flex-wrap justify-end gap-2">{t39}{t42}{t45}</div>;
    $[110] = t39;
    $[111] = t42;
    $[112] = t45;
    $[113] = t46;
  } else {
    t46 = $[113];
  }
  let t47;
  if ($[114] !== t33 || $[115] !== t37 || $[116] !== t4 || $[117] !== t46) {
    t47 = <li className={t4}>{t33}{t37}{t46}</li>;
    $[114] = t33;
    $[115] = t37;
    $[116] = t4;
    $[117] = t46;
    $[118] = t47;
  } else {
    t47 = $[118];
  }
  return t47;
}

// Приблизительный предпросмотр «как увидит покупатель». Ориентир, не пиксель в
// пиксель: hero — первый слайд с заголовком, мозаика — сетка плиток, ряды —
// строка плиток, лента товаров — плейсхолдеры по количеству.
function BlockPreview(t0) {
  const $ = _c(39);
  const {
    kind,
    title,
    slides,
    items,
    productCount
  } = t0;
  const {
    t
  } = useTranslation();
  const mode = contentMode(kind);
  if (mode === "slides") {
    let t1;
    if ($[0] !== slides) {
      t1 = slides.find(_temp);
      $[0] = slides;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const hero = t1;
    let t2;
    if ($[2] !== hero || $[3] !== t) {
      t2 = hero ? <img src={hero.imageUrl} alt="" className="size-full object-cover" /> : <div className="flex size-full items-center justify-center text-xs text-muted-foreground">{t("feed.preview.noImage")}</div>;
      $[2] = hero;
      $[3] = t;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== hero || $[6] !== title) {
      t3 = (hero?.title || title) && <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3"><p className="text-sm font-semibold text-white drop-shadow">{hero?.title || title}</p>{hero?.description && <p className="line-clamp-1 text-xs text-white/85">{hero.description}</p>}</div>;
      $[5] = hero;
      $[6] = title;
      $[7] = t3;
    } else {
      t3 = $[7];
    }
    let t4;
    if ($[8] !== t2 || $[9] !== t3) {
      t4 = <div className="relative aspect-[16/7] w-full max-w-md overflow-hidden rounded-lg border bg-muted">{t2}{t3}</div>;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    return t4;
  }
  if (mode === "tiles") {
    let t1;
    let t2;
    let t3;
    if ($[11] !== items || $[12] !== kind || $[13] !== t) {
      t3 = Symbol.for("react.early_return_sentinel");
      bb0: {
        const withImage = items.filter(_temp2);
        if (withImage.length === 0) {
          let t4;
          if ($[17] !== t) {
            t4 = t("feed.preview.noTiles");
            $[17] = t;
            $[18] = t4;
          } else {
            t4 = $[18];
          }
          let t5;
          if ($[19] !== t4) {
            t5 = <PreviewEmpty label={t4} />;
            $[19] = t4;
            $[20] = t5;
          } else {
            t5 = $[20];
          }
          t3 = t5;
          break bb0;
        }
        if (kind === "collectionMosaic") {
          t3 = <div className="grid max-w-md grid-cols-3 gap-1.5">{withImage.slice(0, 6).map(_temp3)}</div>;
          break bb0;
        }
        t1 = "flex max-w-md gap-2 overflow-hidden";
        t2 = withImage.slice(0, 5).map(_temp4);
      }
      $[11] = items;
      $[12] = kind;
      $[13] = t;
      $[14] = t1;
      $[15] = t2;
      $[16] = t3;
    } else {
      t1 = $[14];
      t2 = $[15];
      t3 = $[16];
    }
    if (t3 !== Symbol.for("react.early_return_sentinel")) {
      return t3;
    }
    let t4;
    if ($[21] !== t1 || $[22] !== t2) {
      t4 = <div className={t1}>{t2}</div>;
      $[21] = t1;
      $[22] = t2;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    return t4;
  }
  if (mode === "products") {
    let t1;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = ["r1", "r2", "r3", "r4"];
      $[24] = t1;
    } else {
      t1 = $[24];
    }
    let t2;
    if ($[25] !== productCount) {
      t2 = t1.slice(0, Math.min(4, Math.max(productCount, 1))).map(_temp5);
      $[25] = productCount;
      $[26] = t2;
    } else {
      t2 = $[26];
    }
    let t3;
    if ($[27] !== productCount || $[28] !== t) {
      t3 = t("feed.productsCount", {
        count: productCount
      });
      $[27] = productCount;
      $[28] = t;
      $[29] = t3;
    } else {
      t3 = $[29];
    }
    let t4;
    if ($[30] !== t3) {
      t4 = <div className="flex items-center px-2 text-xs text-muted-foreground">{t3}</div>;
      $[30] = t3;
      $[31] = t4;
    } else {
      t4 = $[31];
    }
    let t5;
    if ($[32] !== t2 || $[33] !== t4) {
      t5 = <div className="flex max-w-md gap-2 overflow-hidden">{t2}{t4}</div>;
      $[32] = t2;
      $[33] = t4;
      $[34] = t5;
    } else {
      t5 = $[34];
    }
    return t5;
  }
  let t1;
  if ($[35] !== t) {
    t1 = t("feed.preview.personal");
    $[35] = t;
    $[36] = t1;
  } else {
    t1 = $[36];
  }
  let t2;
  if ($[37] !== t1) {
    t2 = <PreviewEmpty label={t1} />;
    $[37] = t1;
    $[38] = t2;
  } else {
    t2 = $[38];
  }
  return t2;
}
function _temp5(k) {
  return <div key={k} className="flex w-24 shrink-0 flex-col gap-1 rounded-md border bg-muted/40 p-1.5"><div className="aspect-[3/4] rounded bg-muted" /><div className="h-2 w-3/4 rounded bg-muted" /><div className="h-2 w-1/2 rounded bg-muted" /></div>;
}
function _temp4(tile_0, i_1) {
  return <div key={`${tile_0.id}_${i_1}`} className="w-20 shrink-0 space-y-1"><div className="aspect-square overflow-hidden rounded-full border bg-muted"><img src={tile_0.imageUrl} alt="" className="size-full object-cover" /></div>{tile_0.label && <p className="truncate text-center text-[10px] text-muted-foreground">{tile_0.label}</p>}</div>;
}
function _temp3(tile, i_0) {
  return <div key={`${tile.id}_${i_0}`} className="aspect-square overflow-hidden rounded-md border bg-muted"><img src={tile.imageUrl} alt="" className="size-full object-cover" /></div>;
}
function _temp2(i) {
  return i.imageUrl.trim();
}
function _temp(s) {
  return s.imageUrl.trim();
}
function PreviewEmpty(t0) {
  const $ = _c(2);
  const {
    label
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="flex h-16 max-w-md items-center justify-center rounded-lg border border-dashed text-xs text-muted-foreground">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function FeedBlockEditor(t0) {
  const $ = _c(222);
  const {
    draft,
    busy,
    onChange,
    onClose,
    onSave
  } = t0;
  const {
    t
  } = useTranslation();
  const isCreate = draft.id === null;
  let t1;
  if ($[0] !== draft.kind) {
    t1 = contentMode(draft.kind);
    $[0] = draft.kind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const mode = t1;
  let t2;
  if ($[2] !== draft || $[3] !== onChange) {
    t2 = (key, value) => onChange({
      ...draft,
      [key]: value
    });
    $[2] = draft;
    $[3] = onChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const set = t2;
  let t3;
  if ($[5] !== draft || $[6] !== onChange) {
    t3 = (i, patch) => onChange({
      ...draft,
      slides: draft.slides.map((s, idx) => idx === i ? {
        ...s,
        ...patch
      } : s)
    });
    $[5] = draft;
    $[6] = onChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const updateSlide = t3;
  let t4;
  if ($[8] !== draft || $[9] !== onChange) {
    t4 = (i_0, patch_0) => onChange({
      ...draft,
      items: draft.items.map((s_0, idx_0) => idx_0 === i_0 ? {
        ...s_0,
        ...patch_0
      } : s_0)
    });
    $[8] = draft;
    $[9] = onChange;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const updateItem = t4;
  let t5;
  if ($[11] !== onClose) {
    t5 = next => !next && onClose();
    $[11] = onClose;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isCreate || $[14] !== t) {
    t6 = isCreate ? t("feed.editor.createTitle") : t("feed.editor.editTitle");
    $[13] = isCreate;
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t6) {
    t7 = <DialogHeader><DialogTitle>{t6}</DialogTitle></DialogHeader>;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("feed.editor.previewLabel");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t8) {
    t9 = <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t8}</p>;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = draft.kind;
  const t11 = draft.title;
  const t12 = draft.slides;
  const t13 = draft.items;
  let t14;
  if ($[22] !== draft.productRows) {
    t14 = draft.productRows.filter(_temp6);
    $[22] = draft.productRows;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== draft.items || $[25] !== draft.kind || $[26] !== draft.slides || $[27] !== draft.title || $[28] !== t14.length) {
    t15 = <BlockPreview kind={t10} title={t11} slides={t12} items={t13} productCount={t14.length} />;
    $[24] = draft.items;
    $[25] = draft.kind;
    $[26] = draft.slides;
    $[27] = draft.title;
    $[28] = t14.length;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== t15 || $[31] !== t9) {
    t16 = <div className="rounded-lg border bg-muted/30 p-3">{t9}{t15}</div>;
    $[30] = t15;
    $[31] = t9;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t) {
    t17 = t("feed.editor.kind");
    $[33] = t;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t17) {
    t18 = <Label>{t17}</Label>;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== draft.kind || $[38] !== isCreate || $[39] !== set || $[40] !== t) {
    t19 = isCreate ? <div className="grid gap-2 sm:grid-cols-2">{KINDS.map(k => {
        const KindIcon = KIND_ICON[k];
        const active = draft.kind === k;
        return <button type="button" key={k} onClick={() => set("kind", k)} className={cn("flex items-start gap-2 rounded-lg border p-3 text-left transition-colors", active ? "border-primary bg-primary/5" : "hover:bg-muted/40")}><span className={cn("flex size-8 shrink-0 items-center justify-center rounded-md", active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}><KindIcon className="size-4" /></span><span className="min-w-0"><span className="block text-sm font-medium">{t(`feed.kind.${k}`)}</span><span className="block text-xs text-muted-foreground">{t(`feed.kindDesc.${k}`)}</span></span></button>;
      })}</div> : <div className="flex items-center gap-2"><Badge variant="outline">{t(`feed.kind.${draft.kind}`)}</Badge><span className="text-xs text-muted-foreground">{t(`feed.kindDesc.${draft.kind}`)}</span></div>;
    $[37] = draft.kind;
    $[38] = isCreate;
    $[39] = set;
    $[40] = t;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t18 || $[43] !== t19) {
    t20 = <div className="space-y-1.5">{t18}{t19}</div>;
    $[42] = t18;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t) {
    t21 = t("feed.editor.segment");
    $[45] = t;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <Label>{t21}</Label>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const t23 = draft.segment ?? "general";
  let t24;
  if ($[49] !== set) {
    t24 = e => set("segment", e.target.value === "general" ? null : e.target.value as FeedSegment);
    $[49] = set;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  let t25;
  if ($[51] !== t) {
    t25 = t("feed.segment.general");
    $[51] = t;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t25) {
    t26 = <option value="general">{t25}</option>;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== t) {
    t27 = SEGMENTS.map(s_1 => <option key={s_1} value={s_1}>{t(`feed.segment.${s_1}`)}</option>);
    $[55] = t;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== t23 || $[58] !== t24 || $[59] !== t26 || $[60] !== t27) {
    t28 = <Select value={t23} onChange={t24} className="w-full">{t26}{t27}</Select>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t26;
    $[60] = t27;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t22 || $[63] !== t28) {
    t29 = <div className="space-y-1.5">{t22}{t28}</div>;
    $[62] = t22;
    $[63] = t28;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== t) {
    t30 = t("feed.editor.title");
    $[65] = t;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t30) {
    t31 = <Label>{t30}</Label>;
    $[67] = t30;
    $[68] = t31;
  } else {
    t31 = $[68];
  }
  let t32;
  if ($[69] !== set) {
    t32 = e_0 => set("title", e_0.target.value);
    $[69] = set;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  let t33;
  if ($[71] !== draft.title || $[72] !== t32) {
    t33 = <Input value={draft.title} onChange={t32} />;
    $[71] = draft.title;
    $[72] = t32;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== t31 || $[75] !== t33) {
    t34 = <div className="space-y-1.5">{t31}{t33}</div>;
    $[74] = t31;
    $[75] = t33;
    $[76] = t34;
  } else {
    t34 = $[76];
  }
  let t35;
  if ($[77] !== t) {
    t35 = t("feed.editor.description");
    $[77] = t;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== t35) {
    t36 = <Label>{t35}</Label>;
    $[79] = t35;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== set) {
    t37 = e_1 => set("description", e_1.target.value);
    $[81] = set;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== draft.description || $[84] !== t37) {
    t38 = <Input value={draft.description} onChange={t37} />;
    $[83] = draft.description;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== t36 || $[87] !== t38) {
    t39 = <div className="space-y-1.5">{t36}{t38}</div>;
    $[86] = t36;
    $[87] = t38;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== t) {
    t40 = t("feed.editor.actionKind");
    $[89] = t;
    $[90] = t40;
  } else {
    t40 = $[90];
  }
  let t41;
  if ($[91] !== t40) {
    t41 = <Label>{t40}</Label>;
    $[91] = t40;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  const t42 = draft.actionKind;
  let t43;
  if ($[93] !== set) {
    t43 = e_2 => set("actionKind", e_2.target.value as Draft["actionKind"]);
    $[93] = set;
    $[94] = t43;
  } else {
    t43 = $[94];
  }
  let t44;
  if ($[95] !== t) {
    t44 = t("feed.editor.actionNone");
    $[95] = t;
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  let t45;
  if ($[97] !== t44) {
    t45 = <option value="">{t44}</option>;
    $[97] = t44;
    $[98] = t45;
  } else {
    t45 = $[98];
  }
  let t46;
  if ($[99] !== t) {
    t46 = t("feed.editor.actionLink");
    $[99] = t;
    $[100] = t46;
  } else {
    t46 = $[100];
  }
  let t47;
  if ($[101] !== t46) {
    t47 = <option value="link">{t46}</option>;
    $[101] = t46;
    $[102] = t47;
  } else {
    t47 = $[102];
  }
  let t48;
  if ($[103] !== t) {
    t48 = t("feed.editor.actionChevron");
    $[103] = t;
    $[104] = t48;
  } else {
    t48 = $[104];
  }
  let t49;
  if ($[105] !== t48) {
    t49 = <option value="chevron">{t48}</option>;
    $[105] = t48;
    $[106] = t49;
  } else {
    t49 = $[106];
  }
  let t50;
  if ($[107] !== draft.actionKind || $[108] !== t43 || $[109] !== t45 || $[110] !== t47 || $[111] !== t49) {
    t50 = <Select value={t42} onChange={t43} className="w-full">{t45}{t47}{t49}</Select>;
    $[107] = draft.actionKind;
    $[108] = t43;
    $[109] = t45;
    $[110] = t47;
    $[111] = t49;
    $[112] = t50;
  } else {
    t50 = $[112];
  }
  let t51;
  if ($[113] !== t41 || $[114] !== t50) {
    t51 = <div className="space-y-1.5">{t41}{t50}</div>;
    $[113] = t41;
    $[114] = t50;
    $[115] = t51;
  } else {
    t51 = $[115];
  }
  let t52;
  if ($[116] !== t) {
    t52 = t("feed.editor.actionLabel");
    $[116] = t;
    $[117] = t52;
  } else {
    t52 = $[117];
  }
  let t53;
  if ($[118] !== t52) {
    t53 = <Label>{t52}</Label>;
    $[118] = t52;
    $[119] = t53;
  } else {
    t53 = $[119];
  }
  let t54;
  if ($[120] !== set) {
    t54 = e_3 => set("actionLabel", e_3.target.value);
    $[120] = set;
    $[121] = t54;
  } else {
    t54 = $[121];
  }
  let t55;
  if ($[122] !== draft.actionLabel || $[123] !== t54) {
    t55 = <Input value={draft.actionLabel} onChange={t54} />;
    $[122] = draft.actionLabel;
    $[123] = t54;
    $[124] = t55;
  } else {
    t55 = $[124];
  }
  let t56;
  if ($[125] !== t53 || $[126] !== t55) {
    t56 = <div className="space-y-1.5">{t53}{t55}</div>;
    $[125] = t53;
    $[126] = t55;
    $[127] = t56;
  } else {
    t56 = $[127];
  }
  let t57;
  if ($[128] !== t51 || $[129] !== t56) {
    t57 = <div className="grid gap-4 sm:grid-cols-2">{t51}{t56}</div>;
    $[128] = t51;
    $[129] = t56;
    $[130] = t57;
  } else {
    t57 = $[130];
  }
  let t58;
  if ($[131] !== set) {
    t58 = e_4 => set("published", e_4.target.checked);
    $[131] = set;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  let t59;
  if ($[133] !== draft.published || $[134] !== t58) {
    t59 = <input type="checkbox" checked={draft.published} onChange={t58} className="size-4" />;
    $[133] = draft.published;
    $[134] = t58;
    $[135] = t59;
  } else {
    t59 = $[135];
  }
  let t60;
  if ($[136] !== t) {
    t60 = t("feed.editor.published");
    $[136] = t;
    $[137] = t60;
  } else {
    t60 = $[137];
  }
  let t61;
  if ($[138] !== t59 || $[139] !== t60) {
    t61 = <label className="flex items-center gap-2 text-sm">{t59}{t60}</label>;
    $[138] = t59;
    $[139] = t60;
    $[140] = t61;
  } else {
    t61 = $[140];
  }
  let t62;
  if ($[141] !== t) {
    t62 = t("feed.editor.content");
    $[141] = t;
    $[142] = t62;
  } else {
    t62 = $[142];
  }
  let t63;
  if ($[143] !== t62) {
    t63 = <Label>{t62}</Label>;
    $[143] = t62;
    $[144] = t63;
  } else {
    t63 = $[144];
  }
  let t64;
  if ($[145] !== draft.slides || $[146] !== mode || $[147] !== set || $[148] !== t) {
    t64 = mode === "slides" && <Button size="sm" variant="outline" onClick={() => set("slides", [...draft.slides, {
      id: rowId("sl"),
      imageUrl: ""
    }])}><Plus className="size-4" /> {t("feed.editor.addSlide")}</Button>;
    $[145] = draft.slides;
    $[146] = mode;
    $[147] = set;
    $[148] = t;
    $[149] = t64;
  } else {
    t64 = $[149];
  }
  let t65;
  if ($[150] !== draft.items || $[151] !== mode || $[152] !== set || $[153] !== t) {
    t65 = mode === "tiles" && <Button size="sm" variant="outline" onClick={() => set("items", [...draft.items, {
      id: rowId("tl"),
      imageUrl: ""
    }])}><Plus className="size-4" /> {t("feed.editor.addTile")}</Button>;
    $[150] = draft.items;
    $[151] = mode;
    $[152] = set;
    $[153] = t;
    $[154] = t65;
  } else {
    t65 = $[154];
  }
  let t66;
  if ($[155] !== draft.productRows || $[156] !== mode || $[157] !== set || $[158] !== t) {
    t66 = mode === "products" && <Button size="sm" variant="outline" onClick={() => set("productRows", [...draft.productRows, {
      id: rowId("pid"),
      value: ""
    }])}><Plus className="size-4" /> {t("feed.editor.addProduct")}</Button>;
    $[155] = draft.productRows;
    $[156] = mode;
    $[157] = set;
    $[158] = t;
    $[159] = t66;
  } else {
    t66 = $[159];
  }
  let t67;
  if ($[160] !== t63 || $[161] !== t64 || $[162] !== t65 || $[163] !== t66) {
    t67 = <div className="mb-2 flex items-center justify-between">{t63}{t64}{t65}{t66}</div>;
    $[160] = t63;
    $[161] = t64;
    $[162] = t65;
    $[163] = t66;
    $[164] = t67;
  } else {
    t67 = $[164];
  }
  let t68;
  if ($[165] !== mode || $[166] !== t) {
    t68 = mode === "none" && <p className="text-sm text-muted-foreground">{t("feed.editor.personalNote")}</p>;
    $[165] = mode;
    $[166] = t;
    $[167] = t68;
  } else {
    t68 = $[167];
  }
  let t69;
  if ($[168] !== draft.slides || $[169] !== mode || $[170] !== set || $[171] !== t || $[172] !== updateSlide) {
    t69 = mode === "slides" && <div className="space-y-3">{draft.slides.length === 0 && <p className="text-sm text-muted-foreground">{t("feed.editor.emptyContent")}</p>}{draft.slides.map((slide, i_1) => <RowShell key={slide.id} preview={slide.imageUrl} index={i_1} total={draft.slides.length} onMove={delta => set("slides", moveInArray(draft.slides, i_1, delta))} onRemove={() => set("slides", draft.slides.filter((_, idx_1) => idx_1 !== i_1))} removeLabel={t("feed.editor.removeRow")} moveUpLabel={t("feed.moveUp")} moveDownLabel={t("feed.moveDown")}><div className="flex items-start gap-2 sm:col-span-2"><Input className="flex-1" placeholder={t("feed.editor.imageUrl")} value={slide.imageUrl} onChange={e_5 => updateSlide(i_1, {
            imageUrl: e_5.target.value
          })} /><ImageUploadButton onUploaded={url => updateSlide(i_1, {
            imageUrl: url
          })} /></div><Input placeholder={t("feed.editor.slideTitle")} value={slide.title ?? ""} onChange={e_6 => updateSlide(i_1, {
          title: e_6.target.value
        })} /><Input placeholder={t("feed.editor.slideDescription")} value={slide.description ?? ""} onChange={e_7 => updateSlide(i_1, {
          description: e_7.target.value
        })} /><Input placeholder={t("feed.editor.categoryId")} value={slide.categoryId ?? ""} onChange={e_8 => updateSlide(i_1, {
          categoryId: e_8.target.value
        })} /><Input placeholder={t("feed.editor.collectionSlug")} value={slide.collectionSlug ?? ""} onChange={e_9 => updateSlide(i_1, {
          collectionSlug: e_9.target.value
        })} /></RowShell>)}</div>;
    $[168] = draft.slides;
    $[169] = mode;
    $[170] = set;
    $[171] = t;
    $[172] = updateSlide;
    $[173] = t69;
  } else {
    t69 = $[173];
  }
  let t70;
  if ($[174] !== draft.items || $[175] !== mode || $[176] !== set || $[177] !== t || $[178] !== updateItem) {
    t70 = mode === "tiles" && <div className="space-y-3">{draft.items.length === 0 && <p className="text-sm text-muted-foreground">{t("feed.editor.emptyContent")}</p>}{draft.items.map((tile, i_2) => <RowShell key={tile.id} preview={tile.imageUrl} index={i_2} total={draft.items.length} onMove={delta_0 => set("items", moveInArray(draft.items, i_2, delta_0))} onRemove={() => set("items", draft.items.filter((__0, idx_2) => idx_2 !== i_2))} removeLabel={t("feed.editor.removeRow")} moveUpLabel={t("feed.moveUp")} moveDownLabel={t("feed.moveDown")}><div className="flex items-start gap-2 sm:col-span-2"><Input className="flex-1" placeholder={t("feed.editor.imageUrl")} value={tile.imageUrl} onChange={e_10 => updateItem(i_2, {
            imageUrl: e_10.target.value
          })} /><ImageUploadButton onUploaded={url_0 => updateItem(i_2, {
            imageUrl: url_0
          })} /></div><Input placeholder={t("feed.editor.label")} value={tile.label ?? ""} onChange={e_11 => updateItem(i_2, {
          label: e_11.target.value
        })} /><Input placeholder={t("feed.editor.categoryId")} value={tile.categoryId ?? ""} onChange={e_12 => updateItem(i_2, {
          categoryId: e_12.target.value
        })} /><Input placeholder={t("feed.editor.collectionSlug")} value={tile.collectionSlug ?? ""} onChange={e_13 => updateItem(i_2, {
          collectionSlug: e_13.target.value
        })} /></RowShell>)}</div>;
    $[174] = draft.items;
    $[175] = mode;
    $[176] = set;
    $[177] = t;
    $[178] = updateItem;
    $[179] = t70;
  } else {
    t70 = $[179];
  }
  let t71;
  if ($[180] !== draft.productRows || $[181] !== mode || $[182] !== set || $[183] !== t) {
    t71 = mode === "products" && <div className="space-y-2">{draft.productRows.length === 0 && <p className="text-sm text-muted-foreground">{t("feed.editor.emptyContent")}</p>}{draft.productRows.map((row, i_3) => <div key={row.id} className="flex items-center gap-2"><span className="w-6 text-center text-xs tabular-nums text-muted-foreground">{i_3 + 1}</span><Input placeholder={t("feed.editor.productId")} value={row.value} onChange={e_14 => set("productRows", draft.productRows.map((x, idx_3) => idx_3 === i_3 ? {
          ...x,
          value: e_14.target.value
        } : x))} /><div className="flex"><Button size="icon-sm" variant="ghost" aria-label={t("feed.moveUp")} disabled={i_3 === 0} onClick={() => set("productRows", moveInArray(draft.productRows, i_3, -1))}><ArrowUp className="size-4" /></Button><Button size="icon-sm" variant="ghost" aria-label={t("feed.moveDown")} disabled={i_3 === draft.productRows.length - 1} onClick={() => set("productRows", moveInArray(draft.productRows, i_3, 1))}><ArrowDown className="size-4" /></Button></div><Button size="icon-sm" variant="ghost" aria-label={t("feed.editor.removeRow")} onClick={() => set("productRows", draft.productRows.filter((__1, idx_4) => idx_4 !== i_3))}><X className="size-4" /></Button></div>)}</div>;
    $[180] = draft.productRows;
    $[181] = mode;
    $[182] = set;
    $[183] = t;
    $[184] = t71;
  } else {
    t71 = $[184];
  }
  let t72;
  if ($[185] !== t67 || $[186] !== t68 || $[187] !== t69 || $[188] !== t70 || $[189] !== t71) {
    t72 = <div className="border-t pt-4">{t67}{t68}{t69}{t70}{t71}</div>;
    $[185] = t67;
    $[186] = t68;
    $[187] = t69;
    $[188] = t70;
    $[189] = t71;
    $[190] = t72;
  } else {
    t72 = $[190];
  }
  let t73;
  if ($[191] !== t16 || $[192] !== t20 || $[193] !== t29 || $[194] !== t34 || $[195] !== t39 || $[196] !== t57 || $[197] !== t61 || $[198] !== t72) {
    t73 = <div className="space-y-4">{t16}{t20}{t29}{t34}{t39}{t57}{t61}{t72}</div>;
    $[191] = t16;
    $[192] = t20;
    $[193] = t29;
    $[194] = t34;
    $[195] = t39;
    $[196] = t57;
    $[197] = t61;
    $[198] = t72;
    $[199] = t73;
  } else {
    t73 = $[199];
  }
  let t74;
  if ($[200] !== t) {
    t74 = t("feed.editor.cancel");
    $[200] = t;
    $[201] = t74;
  } else {
    t74 = $[201];
  }
  let t75;
  if ($[202] !== busy || $[203] !== onClose || $[204] !== t74) {
    t75 = <Button variant="outline" onClick={onClose} disabled={busy}>{t74}</Button>;
    $[202] = busy;
    $[203] = onClose;
    $[204] = t74;
    $[205] = t75;
  } else {
    t75 = $[205];
  }
  let t76;
  if ($[206] !== t) {
    t76 = t("feed.editor.save");
    $[206] = t;
    $[207] = t76;
  } else {
    t76 = $[207];
  }
  let t77;
  if ($[208] !== busy || $[209] !== onSave || $[210] !== t76) {
    t77 = <Button onClick={onSave} disabled={busy}>{t76}</Button>;
    $[208] = busy;
    $[209] = onSave;
    $[210] = t76;
    $[211] = t77;
  } else {
    t77 = $[211];
  }
  let t78;
  if ($[212] !== t75 || $[213] !== t77) {
    t78 = <DialogFooter>{t75}{t77}</DialogFooter>;
    $[212] = t75;
    $[213] = t77;
    $[214] = t78;
  } else {
    t78 = $[214];
  }
  let t79;
  if ($[215] !== t7 || $[216] !== t73 || $[217] !== t78) {
    t79 = <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-3xl">{t7}{t73}{t78}</DialogContent>;
    $[215] = t7;
    $[216] = t73;
    $[217] = t78;
    $[218] = t79;
  } else {
    t79 = $[218];
  }
  let t80;
  if ($[219] !== t5 || $[220] !== t79) {
    t80 = <Dialog open={true} onOpenChange={t5}>{t79}</Dialog>;
    $[219] = t5;
    $[220] = t79;
    $[221] = t80;
  } else {
    t80 = $[221];
  }
  return t80;
}

// Кнопка «Загрузить фото»: скрытый input[type=file] + аплоуд на бэкенд, по
// завершении подставляет полученный URL в imageUrl строки. URL-поле рядом
// остаётся — работают оба способа.
function _temp6(r) {
  return r.value.trim();
}
function ImageUploadButton({
  onUploaded,
  disabled
}: {
  onUploaded: (url: string) => void;
  disabled?: boolean;
}) {
  const {
    t
  } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setPending(true);
    setError(null);
    try {
      onUploaded(await api.feed.uploadMedia(file));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  return <div className="flex shrink-0 flex-col items-end gap-1">
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={e_0 => void onPick(e_0.target.files?.[0])} />
      <Button type="button" size="sm" variant="outline" disabled={disabled || pending} onClick={() => inputRef.current?.click()}>
        <Upload className="size-4" />
        {pending ? t("feed.editor.uploading") : t("feed.editor.uploadImage")}
      </Button>
      {error && <p className="max-w-40 text-right text-xs text-destructive">{error}</p>}
    </div>;
}
function RowShell(t0) {
  const $ = _c(35);
  const {
    preview,
    index,
    total,
    onMove,
    onRemove,
    removeLabel,
    moveUpLabel,
    moveDownLabel,
    children
  } = t0;
  const t1 = index + 1;
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="text-xs tabular-nums text-muted-foreground">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== preview) {
    t3 = <div className="size-16 shrink-0 overflow-hidden rounded bg-muted">{preview ? <img src={preview} alt="" className="size-full object-cover" /> : <div className="size-full" />}</div>;
    $[2] = preview;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className="flex flex-col items-center gap-1">{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== children) {
    t5 = <div className="grid flex-1 gap-2 sm:grid-cols-2">{children}</div>;
    $[7] = children;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = index === 0;
  let t7;
  if ($[9] !== onMove) {
    t7 = () => onMove(-1);
    $[9] = onMove;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowUp className="size-4" />;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== moveUpLabel || $[13] !== t6 || $[14] !== t7) {
    t9 = <Button size="icon-sm" variant="ghost" aria-label={moveUpLabel} disabled={t6} onClick={t7}>{t8}</Button>;
    $[12] = moveUpLabel;
    $[13] = t6;
    $[14] = t7;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = index === total - 1;
  let t11;
  if ($[16] !== onMove) {
    t11 = () => onMove(1);
    $[16] = onMove;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ArrowDown className="size-4" />;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== moveDownLabel || $[20] !== t10 || $[21] !== t11) {
    t13 = <Button size="icon-sm" variant="ghost" aria-label={moveDownLabel} disabled={t10} onClick={t11}>{t12}</Button>;
    $[19] = moveDownLabel;
    $[20] = t10;
    $[21] = t11;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <X className="size-4" />;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== onRemove || $[25] !== removeLabel) {
    t15 = <Button size="icon-sm" variant="ghost" aria-label={removeLabel} onClick={onRemove}>{t14}</Button>;
    $[24] = onRemove;
    $[25] = removeLabel;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t13 || $[28] !== t15 || $[29] !== t9) {
    t16 = <div className="flex flex-col">{t9}{t13}{t15}</div>;
    $[27] = t13;
    $[28] = t15;
    $[29] = t9;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16 || $[32] !== t4 || $[33] !== t5) {
    t17 = <div className="flex gap-3 rounded-md border p-3">{t4}{t5}{t16}</div>;
    $[31] = t16;
    $[32] = t4;
    $[33] = t5;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  return t17;
}